import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Grid,
  IconButton,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Snackbar,
  Alert,
  ListItemIcon,
  Paper,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ImageIcon from '@mui/icons-material/Image';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import MDEditor from '@uiw/react-md-editor';

function KnowledgeBase() {
  const [file, setFile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [newDocumentName, setNewDocumentName] = useState('');
  const [newDocumentContent, setNewDocumentContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [datasetId, setDatasetId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    const shortToken = sessionStorage.getItem('shortToken') || localStorage.getItem('shortToken');
    if (shortToken) {
      setToken(shortToken);
      connectDatastore(shortToken);
    } else {
      setSnackbar({ open: true, message: 'No se encontró token de autenticación', severity: 'error' });
    }
  }, []);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const connectDatastore = async (shortToken) => {
    setLoading(true);
    try {
      const response = await axios.post(
        '/api/kb/connect',
        {},
        {
          headers: { Authorization: `Bearer ${shortToken}` },
        }
      );

      setDatasetId(response.data.datasetId);
      setIsConnected(true);
      setSnackbar({ open: true, message: 'Datastore interno conectado', severity: 'success' });
      await fetchDocuments(shortToken);
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error al conectar datastore interno: ${error.response?.data?.details || error.message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (shortTokenParam = token) => {
    if (!shortTokenParam) return;

    setLoading(true);
    try {
      const response = await axios.get('/api/kb/documents', {
        headers: { Authorization: `Bearer ${shortTokenParam}` },
      });
      setDocuments(response.data.data || []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error al obtener documentos: ${error.response?.data?.details || error.message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const createDocument = async (event) => {
    event.preventDefault();
    if (!newDocumentContent.trim()) {
      setSnackbar({ open: true, message: 'El contenido no puede estar vacío', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        '/api/kb/documents/text',
        {
          name: newDocumentName || `Documento ${new Date().toLocaleString()}`,
          text: newDocumentContent,
        },
        { headers: authHeaders }
      );

      setNewDocumentName('');
      setNewDocumentContent('');
      setSnackbar({ open: true, message: 'Documento creado con éxito', severity: 'success' });
      await fetchDocuments();
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error al crear documento: ${error.response?.data?.details || error.message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const createDocumentByFile = async (event) => {
    event.preventDefault();
    if (!file) {
      setSnackbar({ open: true, message: 'Selecciona un archivo', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post('/api/kb/documents/file', formData, {
        headers: {
          ...authHeaders,
          'Content-Type': 'multipart/form-data',
        },
      });

      setFile(null);
      setSnackbar({ open: true, message: 'Archivo subido con éxito', severity: 'success' });
      await fetchDocuments();
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error al subir archivo: ${error.response?.data?.details || error.message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteDocument = async (documentId) => {
    try {
      await axios.delete(`/api/kb/documents/${documentId}`, { headers: authHeaders });
      await fetchDocuments();
      setSnackbar({ open: true, message: 'Documento eliminado con éxito', severity: 'success' });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error al eliminar documento: ${error.response?.data?.details || error.message}`,
        severity: 'error',
      });
    }
  };

  const getDocumentIcon = (dataSourceType = '') => {
    const type = String(dataSourceType).toLowerCase();
    if (type.includes('pdf')) return <PictureAsPdfIcon />;
    if (type.includes('csv') || type.includes('excel')) return <TableChartIcon />;
    if (type.includes('image')) return <ImageIcon />;
    return <DescriptionIcon />;
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  };

  return (
    <Container maxWidth="lg">
      <Box my={4}>
        <Typography variant="h4" gutterBottom align="center">
          Base de Conocimientos
        </Typography>

        {!isConnected ? (
          <Box display="flex" justifyContent="center" my={4}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<LinkIcon />}
              onClick={() => connectDatastore(token)}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Conectar Datastore'}
            </Button>
          </Box>
        ) : (
          <Paper elevation={3}>
            <Box p={3}>
              <Typography variant="body2" color="textSecondary" mb={2}>
                Dataset interno: <strong>{datasetId}</strong>
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Crear documento de texto
                  </Typography>
                  <form onSubmit={createDocument}>
                    <TextField
                      fullWidth
                      value={newDocumentName}
                      onChange={(e) => setNewDocumentName(e.target.value)}
                      label="Nombre del documento"
                      margin="normal"
                      variant="outlined"
                    />
                    <Typography variant="subtitle1" gutterBottom>
                      Contenido del documento
                    </Typography>
                    <MDEditor
                      value={newDocumentContent}
                      onChange={(value) => setNewDocumentContent(value || '')}
                      preview="edit"
                      height={300}
                    />
                    <Box mt={2}>
                      <Button type="submit" variant="contained" color="primary" startIcon={<CloudUploadIcon />} fullWidth>
                        Crear Documento
                      </Button>
                    </Box>
                  </form>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Subir archivo
                  </Typography>
                  <form onSubmit={createDocumentByFile}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<FileUploadIcon />}
                      fullWidth
                      style={{ marginBottom: '1rem' }}
                    >
                      Seleccionar Archivo
                      <input
                        type="file"
                        hidden
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        required
                      />
                    </Button>
                    {file && (
                      <Typography variant="body2" gutterBottom>
                        Archivo seleccionado: {file.name}
                      </Typography>
                    )}
                    <Button type="submit" variant="contained" color="primary" fullWidth disabled={!file}>
                      Subir Archivo
                    </Button>
                  </form>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        )}

        {isConnected && (
          <Box mt={4}>
            <Paper elevation={3}>
              <Box p={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Lista de documentos</Typography>
                  <Button startIcon={<RefreshIcon />} onClick={() => fetchDocuments()} disabled={loading}>
                    Actualizar
                  </Button>
                </Box>
                <Divider />
                {loading ? (
                  <Box display="flex" justifyContent="center" my={2}>
                    <CircularProgress />
                  </Box>
                ) : documents.length > 0 ? (
                  <List>
                    {documents.map((doc) => (
                      <ListItem key={doc.id}>
                        <ListItemIcon>{getDocumentIcon(doc.dataSourceType)}</ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center">
                              {doc.name}
                              <FiberManualRecordIcon
                                style={{ color: 'green', fontSize: 12, marginLeft: 8 }}
                              />
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography component="span" variant="body2" color="textPrimary">
                                Tipo: {doc.dataSourceType || 'text'} | Chunks: {doc.chunkCount || 0}
                              </Typography>
                              <br />
                              <Typography component="span" variant="body2" color="textSecondary">
                                Creado: {formatDate(doc.createdAt)} | Palabras: {doc.wordCount || 0}
                              </Typography>
                            </>
                          }
                        />
                        <IconButton onClick={() => deleteDocument(doc.id)} color="secondary">
                          <DeleteIcon />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body1" align="center">
                    No hay documentos disponibles. Crea o sube un documento para comenzar.
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default KnowledgeBase;
